import { CbhIcon, Text, TextArea } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, MenuItem, Select, Stack } from "@mui/material";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import pluralize from "pluralize";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { z } from "zod";

import { jobTypeOptions, shiftTypeOptions } from "../../../PlacementCandidate/constants";
import { FilterChips } from "../FilterChips";

export const applicationPreferencesSchema = z.object({
  yearsOfExperience: z.record(z.string(), z.number(), {
    required_error: "Select years of experience",
  }),
  jobType: z.string({ required_error: "Select at least one job type" }).min(1, {
    message: "Select at least one job type",
  }),
  shiftTime: z.string({ required_error: "Select at least one shift time" }).min(1, {
    message: "Select at least one shift time",
  }),
  daysOfWeek: z.string({ required_error: "Select at least one day of the week" }).min(1, {
    message: "Select at least one day of the week",
  }),
  additionalNotes: z
    .string({
      required_error: "Enter additional notes",
    })
    .max(300, {
      message: "Maximum length is 300 characters",
    }),
});

export type ApplicationPreferencesFormData = z.infer<typeof applicationPreferencesSchema>;

interface WorkerPreferencesStepProps {
  applicationQualification: string | undefined;
  onFieldChange: (
    fieldName: keyof ApplicationPreferencesFormData,
    value: ApplicationPreferencesFormData[keyof ApplicationPreferencesFormData]
  ) => void;
}

export function WorkerPreferencesStep(props: WorkerPreferencesStepProps) {
  const { applicationQualification, onFieldChange } = props;

  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<ApplicationPreferencesFormData>();

  // Subscribe to all form field changes and propagate via onFieldChange callback
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name) {
        const newValue = values[name as keyof ApplicationPreferencesFormData];
        if (isDefined(newValue)) {
          onFieldChange(
            name as keyof ApplicationPreferencesFormData,
            newValue as ApplicationPreferencesFormData[keyof ApplicationPreferencesFormData]
          );
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, onFieldChange]);

  return (
    <Box sx={{ px: 5, py: 6 }}>
      <Stack spacing={8}>
        {/* Title */}
        <Stack spacing={5}>
          <Text variant="h3" textAlign="center">
            Review and confirm key details
          </Text>
          <Text gutterBottom variant="body1" textAlign="center">
            Let the facility know what schedule you&apos;d like to work for this job.
          </Text>
        </Stack>

        <Stack spacing={7}>
          {applicationQualification && (
            <Controller
              control={control}
              name="yearsOfExperience"
              render={({ field }) => {
                const yearsOptions = Array.from({ length: 26 }, (_, index) => index);

                return (
                  <Stack spacing={5}>
                    <Text variant="body1">My years of experience:</Text>
                    <Select
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-input": {
                          p: 5,
                        },
                        "& .MuiSelect-select": {
                          paddingRight: "10px !important",
                        },
                      }}
                      size="medium"
                      variant="outlined"
                      value={field.value?.[applicationQualification]?.toString() ?? "-1"}
                      IconComponent={() => (
                        <Box sx={{ pr: 4 }}>
                          <CbhIcon type="chevron-down" size="small" />
                        </Box>
                      )}
                      renderValue={(selected) => {
                        if (selected === "-1") {
                          return (
                            <Stack direction="row" width="100%" justifyContent="space-between">
                              <Text variant="body1">{applicationQualification}</Text>

                              <Text variant="body1">years</Text>
                            </Stack>
                          );
                        }

                        const yearsLabel =
                          selected === "25"
                            ? `${selected} years +`
                            : `${selected} ${pluralize("year", Number(selected))}`;

                        return (
                          <Stack direction="row" width="100%" justifyContent="space-between">
                            <Text variant="body1">{applicationQualification ?? ""}</Text>

                            <Text variant="body1">{yearsLabel}</Text>
                          </Stack>
                        );
                      }}
                      onChange={async (event) => {
                        const parsed = Number.parseInt(event.target.value, 10);
                        const newValue: Record<string, number> = {
                          [applicationQualification]: parsed,
                        };
                        field.onChange(newValue);
                      }}
                    >
                      <MenuItem disabled value="-1">
                        Please select a value
                      </MenuItem>
                      {yearsOptions.map((yr) => (
                        <MenuItem key={yr} value={yr.toString()}>
                          {yr === 25 ? `${yr} years +` : `${yr} ${pluralize("year", yr)}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.yearsOfExperience && (
                      <Text variant="body2" color={(theme) => theme.palette.error.main}>
                        {/* FIXME: Automatically disabled during ESLint config update */}
                        {/* eslint-disable-next-line @typescript-eslint/no-base-to-string */}
                        {errors.yearsOfExperience.message?.toString()}
                      </Text>
                    )}
                  </Stack>
                );
              }}
            />
          )}

          {/* Job Type Options */}
          <FilterChips
            name="jobType"
            control={control}
            options={jobTypeOptions}
            title="I am looking for:"
            error={errors.jobType}
          />

          {/* Shift Options */}
          <FilterChips
            name="shiftTime"
            control={control}
            options={shiftTypeOptions}
            title="I can do:"
            error={errors.shiftTime}
          />

          {/* Days available */}
          <FilterChips
            multiSelect
            name="daysOfWeek"
            control={control}
            options={[
              {
                label: "Mon",
                value: DayOfTheWeek.MONDAY,
              },
              {
                label: "Tue",
                value: DayOfTheWeek.TUESDAY,
              },
              {
                label: "Wed",
                value: DayOfTheWeek.WEDNESDAY,
              },
              {
                label: "Thu",
                value: DayOfTheWeek.THURSDAY,
              },
              {
                label: "Fri",
                value: DayOfTheWeek.FRIDAY,
              },
              {
                label: "Sat",
                value: DayOfTheWeek.SATURDAY,
              },
              {
                label: "Sun",
                value: DayOfTheWeek.SUNDAY,
              },
            ]}
            title="I can work:"
            error={errors.daysOfWeek}
          />

          {/* Additional notes */}
          <Controller
            control={control}
            name="additionalNotes"
            render={({ field }) => (
              <Stack spacing={4}>
                <TextArea
                  rows={4}
                  {...field}
                  placeholder={`Tell the facility why you're looking for this schedule. For example, "Looking for AM shifts so I can spend time with my kids in the evening."`}
                />
                {errors.additionalNotes && (
                  <Text variant="body2" color={(theme) => theme.palette.error.main}>
                    {errors.additionalNotes.message!}
                  </Text>
                )}
              </Stack>
            )}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
